import React, { useEffect, useState, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Statistic,
  Typography,
  Spin,
  List,
  Space,
  Tag,
  Button,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { privateAdminGetApi } from "../../utils/apiHelper";

const { Title, Text } = Typography;

function StatusTag({ ok, label }) {
  return (
    <Tag color={ok ? "green" : "red"} style={{ marginRight: 0 }}>
      {label}: {ok ? "OK" : "DOWN"}
    </Tag>
  );
}

function SystemHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(() => {
    setLoading(true);
    privateAdminGetApi("admin/health")
      .then((res) => {
        setHealth(res.data.result);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchHealth();
  }, [fetchHealth]);

  return (
    <Card size="small" title="System health">
      <Space align="center" wrap>
        {loading || !health ? (
          <Spin size="small" />
        ) : (
          <>
            <StatusTag ok={health.api.mongo} label="API / Mongo" />
            <StatusTag ok={health.api.rabbitmq} label="API / RabbitMQ" />
            <StatusTag ok={health.mqs.reachable} label="MQs reachable" />
            <StatusTag ok={health.mqs.mongo} label="MQs / Mongo" />
            <StatusTag ok={health.mqs.rabbitmq} label="MQs / RabbitMQ" />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Checked {new Date(health.checkedAt).toLocaleTimeString()}
            </Text>
          </>
        )}
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={fetchHealth}
          loading={loading}
        />
      </Space>
    </Card>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    privateAdminGetApi("admin/dashboard")
      .then((res) => {
        setData(res.data.result);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (!data) {
    return <Text type="secondary">Failed to load dashboard.</Text>;
  }

  const chartData = data.signupsOverTime.map((row) => ({
    date: row._id,
    count: row.count,
  }));

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Title level={5} style={{ marginBottom: 0 }}>
        Platform overview
      </Title>

      <SystemHealth />

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic title="Total organizations" value={data.totalOrgs} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic title="New this week" value={data.orgsThisWeek} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic title="Total users" value={data.totalUsers} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic title="Total contacts" value={data.totalContacts} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Emails sent this month"
              value={data.emailsThisMonth.success}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Emails failed this month"
              value={data.emailsThisMonth.failed + data.emailsThisMonth.invalid}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Form submissions this month"
              value={data.submissionsThisMonth.confirmed}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Spam rate this month"
              value={Math.round(data.spamRate * 100)}
              suffix="%"
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" title="Signups (last 90 days)">
        {chartData.length ? (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid stroke="#E8ECEF" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#4A5A61"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <Text type="secondary">No signups in the last 90 days.</Text>
        )}
      </Card>

      <Card size="small" title="Most recently active organizations">
        <List
          dataSource={data.mostActiveOrganizations}
          renderItem={(org) => (
            <List.Item>
              <Text>{org.name}</Text>
              <Text type="secondary">
                {new Date(org.lastActiveAt).toLocaleString()}
              </Text>
            </List.Item>
          )}
        />
      </Card>
    </Space>
  );
}
